/**
 * FhirJsonPreview - collapsible JSON preview for the active instrument's
 * QuestionnaireResponse + Observation pair (cardiometabolic + longevity
 * expansion 2026-05-07).
 *
 * Anchored to the prototype's "Output Preview - FHIR
 * QuestionnaireResponse JSON structure" panel
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html PROMs
 * tab). The prototype shows a structured JSON output anchored to the
 * dispatched questionnaire; this build reuses the canonical
 * `promToFhir` mapper from `services/fhir-mappers/prom-to-questionnaire-
 * response.ts` (Sprint 2.2) so the preview is the exact pair the FHIR
 * client lands on submission.
 *
 * HC1 reproducibility: the preview is deterministic. Inputs are the
 *   selected definition, the active patient + tenant, and a fixed
 *   `nowIso`. Two identical inputs yield byte-identical JSON.
 *
 * HC2: tokens-only - no inline color literals.
 * HC5: zero em / en dashes anywhere.
 */
import { useMemo, useState } from 'react';
import { promToFhir, type PromSubmission } from '../../services/fhir-mappers';
import { usePatientStore } from '../../state/patient.store';
import { useTenantStore } from '../../state/tenant.store';
import {
  PROM_DEFINITION_BY_ID,
  type PromDefinition,
  type PromId,
} from './prom-definitions';

export interface FhirJsonPreviewProps {
  /** Selected PROM id. If null, the preview shows an empty hint. */
  selected: PromId | null;
  /**
   * Optional clock injection for deterministic snapshots in tests. When
   * omitted, the preview still renders deterministically because it
   * uses a fixed Demo-Mode authored timestamp anchored to the current
   * date so two consecutive renders agree.
   */
  nowIso?: () => string;
  /** Optional class hook for layout-specific overrides. */
  className?: string;
}

/**
 * Default authored timestamp for the preview snapshot. The preview is a
 * read-only sample (no submission lands), so a fixed Demo-Mode clock
 * keeps the JSON stable across renders. The actual PromCard submission
 * uses `Date.now` (or the injected clock) so the live FHIR pair carries
 * a real authored timestamp.
 */
const PREVIEW_DEFAULT_NOW_ISO = '2026-05-07T00:00:00.000Z';

/**
 * Build a synthetic Demo-Mode submission for the given definition. The
 * preview answers every item with a midpoint value (the median of the
 * Likert option values) so the JSON is well-formed without requiring
 * the user to fill the form first.
 */
function buildPreviewSubmission(
  def: PromDefinition,
  patientReference: string,
  tenantId: string,
  authored: string,
): PromSubmission {
  let total = 0;
  const items = def.questions.map((q) => {
    const sortedValues = q.options.map((o) => o.value).sort((a, b) => a - b);
    const midpoint = sortedValues[Math.floor(sortedValues.length / 2)] ?? 0;
    total += midpoint;
    return {
      linkId: q.linkId,
      text: q.text,
      value: midpoint,
    };
  });

  return {
    submissionId: `${def.id}-preview`,
    questionnaireUrl: def.questionnaireUrl,
    patientReference,
    tenantId,
    authored,
    items,
    score: {
      loincCode: def.loincCode,
      display: def.loincDisplay,
      value: total,
      unit: def.scoreUnit,
    },
  };
}

export function FhirJsonPreview({
  selected,
  nowIso = () => PREVIEW_DEFAULT_NOW_ISO,
  className,
}: FhirJsonPreviewProps) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const [expanded, setExpanded] = useState(true);

  const json = useMemo(() => {
    if (!selected) return null;
    const def = PROM_DEFINITION_BY_ID[selected];
    if (!def) return null;
    const patientReference = activePatientId
      ? `Patient/${activePatientId}`
      : 'Patient/anonymous';
    const submission = buildPreviewSubmission(
      def,
      patientReference,
      tenantId,
      nowIso(),
    );
    const pair = promToFhir(submission);
    return JSON.stringify(pair, null, 2);
  }, [selected, tenantId, activePatientId, nowIso]);

  return (
    <section
      aria-labelledby="fhir-json-preview-title"
      data-testid="fhir-json-preview"
      data-prom-selected={selected ?? ''}
      className={[
        'flex flex-col gap-gutter-sm rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex items-center justify-between gap-gutter-sm">
        <div className="flex flex-col gap-1">
          <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
            FHIR Output Preview
          </p>
          <h3
            id="fhir-json-preview-title"
            className="text-signal-base font-semibold text-text-primary"
          >
            QuestionnaireResponse + Observation pair
          </h3>
        </div>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="fhir-json-preview-body"
          data-testid="fhir-json-preview-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-medium text-text-secondary hover:bg-surface-muted"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </header>

      {expanded ? (
        json ? (
          <pre
            id="fhir-json-preview-body"
            data-testid="fhir-json-preview-body"
            className="max-h-96 overflow-auto rounded-signal-md bg-surface-muted p-gutter-md text-signal-xs text-text-primary"
          >
            {json}
          </pre>
        ) : (
          <p
            data-testid="fhir-json-preview-empty"
            className="text-signal-sm text-text-tertiary"
          >
            Pick an instrument from the schedule or the right-rail plan
            to render the FHIR JSON pair.
          </p>
        )
      ) : null}
    </section>
  );
}
